import { useState } from "react";
import { Copy, Check, Terminal, Download, ExternalLink } from "lucide-react";

import { ImagoModal, ImagoModalBody, ImagoModalFooter } from "../ui/ImagoModal";
import { ImagoButton } from "../ui/ImagoButton";
import { toast } from "react-toastify";
import { readToken } from '../../auth/session';

/**
 * Modal dialog for generating agent tokens, downloading the agent JAR,
 * and showing setup instructions with pre-filled commands.
 */
const AgentTokenDialog = ({ isOpen, onClose }) => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const serverUrl = `wss://${window.location.host}/api/ws/agent`;

  const generateToken = async () => {
    setLoading(true);
    try {
      const authToken = readToken();
      const response = await fetch("/api/agent/token", {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        toast.success("Agent token generated successfully");
      } else {
        toast.error("Failed to generate agent token");
      }
    } catch (err) {
      toast.error("Error generating token: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const fullCommand = token
    ? `java -jar imago-agent.jar --server=${serverUrl} --token=${token}`
    : null;

  const handleClose = () => {
    setToken(null);
    setCopiedField(null);
    onClose();
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch("/api/agent/download");
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "imago-agent.jar";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download agent: " + err.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <ImagoModal isOpen={isOpen} onClose={handleClose} title="Local Agent Setup" size="lg">
      <ImagoModalBody>
        <div className="space-y-5">
          {/* Description */}
          <div className="p-3 bg-card rounded-lg border border-border">
            <p className="text-sm text-foreground-secondary">
              The Imago Local Agent lets you test APIs running on your machine (localhost, 127.0.0.1, etc.)
              directly from the Imago UI. No code or repo access needed.
            </p>
          </div>

          {/* Step 1: Download */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-bold">1</span>
              Download the Agent
            </h4>
            <div className="flex items-center gap-3">
              <ImagoButton variant="primary" onClick={handleDownload} loading={downloading} className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Download imago-agent.jar
              </ImagoButton>
              <span className="text-xs text-muted flex items-center gap-1">
                Requires Java 21+
                <a
                  href="https://adoptium.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-0.5"
                >
                  Get Java <ExternalLink className="w-3 h-3" />
                </a>
              </span>
            </div>
          </div>

          {/* Step 2: Generate token */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-bold">2</span>
              Generate Agent Token
            </h4>
            {!token ? (
              <ImagoButton variant="secondary" onClick={generateToken} loading={loading}>
                Generate Token
              </ImagoButton>
            ) : (
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-background rounded border border-border text-xs font-mono text-foreground break-all select-all">
                  {token}
                </code>
                <button
                  onClick={() => copyToClipboard(token, "token")}
                  className="p-2 rounded-md hover:bg-card transition-colors text-foreground-secondary"
                  title="Copy token"
                  aria-label="Copy token to clipboard"
                >
                  {copiedField === "token" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            )}
            {token && (
              <p className="text-xs text-muted mt-1">Expires in 24 hours. Generating a new one invalidates the previous.</p>
            )}
          </div>

          {/* Step 3: Run */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-bold">3</span>
              Run the Agent
            </h4>
            <p className="text-sm text-foreground-secondary mb-2">
              Open a terminal, navigate to where you saved the JAR, and run:
            </p>
            <div className="relative group">
              <div className="flex items-start gap-2 p-3 bg-background rounded-lg border border-border">
                <Terminal className="w-4 h-4 text-muted flex-shrink-0 mt-0.5" />
                <code className="text-xs font-mono text-foreground break-all">
                  {token
                    ? `java -jar imago-agent.jar --server=${serverUrl} --token=${token}`
                    : `java -jar imago-agent.jar --server=${serverUrl} --token=YOUR_TOKEN`}
                </code>
              </div>
              {token && (
                <button
                  onClick={() => copyToClipboard(fullCommand, "command")}
                  className="absolute top-2 right-2 p-1.5 rounded-md bg-card/80 hover:bg-card transition-colors text-foreground-secondary opacity-0 group-hover:opacity-100"
                  title="Copy command"
                  aria-label="Copy run command to clipboard"
                >
                  {copiedField === "command" ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              )}
            </div>
            <p className="text-xs text-muted mt-2">
              After the first run, your config is saved to <code className="bg-background px-1 py-0.5 rounded">~/.imago/agent.properties</code>.
              Next time just run <code className="bg-background px-1 py-0.5 rounded">java -jar imago-agent.jar</code>.
            </p>
          </div>
        </div>
      </ImagoModalBody>

      <ImagoModalFooter>
        <ImagoButton variant="secondary" onClick={handleClose}>
          Close
        </ImagoButton>
      </ImagoModalFooter>
    </ImagoModal>
  );
};

export default AgentTokenDialog;
